import { useEffect } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { DEVICES_QUERY } from '@scio3/graphql-client';
import { SORT_DIRECTION } from '@scio3/shared-types';
import { useTeamStore } from '@/stores/team-store';
import { useAuthStore } from '@/stores/auth-store';
import { useAppVariant } from '@/lib/app-variant';

/**
 * Idle-time prefetch for the MDM Devices list.
 *
 * Companion to `prefetchRouteChunks` (`lib/prefetch-routes.ts`) which warms
 * the JS chunk. This hook warms the Apollo cache by firing `DEVICES_QUERY`
 * with the page's default variables, gated on auth + the `mdm` company
 * feature flag (or the optiedge runtime variant where MDM is always on).
 *
 * Net effect: when the user clicks "Devices", both the chunk and the data
 * are already resident — first paint feels instant on top of the existing
 * `cache-and-network` policy.
 *
 * `cache-first` so the prefetch is a no-op when the cache is already warm
 * (e.g. after IndexedDB hydration on a returning session).
 * `requestIdleCallback` so we don't compete with whatever the user is
 * currently doing on Screens / Assets / etc.
 *
 * Mount once from `AppShell` after auth resolves.
 */
export function useMdmPrefetch(): void {
  const client = useApolloClient();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const company = useAuthStore((s) => s.company);
  const variant = useAppVariant();

  useEffect(() => {
    if (!activeTeamId) return;

    // OptiEdge variant always has MDM. On optisigns, gate on the new
    // company.feature.mdm flag (not yet typed in CompanyFeatures — read
    // structurally, mirroring `routes/MdmFeatureRoute.tsx:33`).
    const mdmEnabled =
      variant === 'optiedge' ||
      (company?.feature as Record<string, unknown> | undefined)?.mdm === true;
    if (!mdmEnabled) return;

    const run = () => {
      client
        .query({
          query: DEVICES_QUERY,
          variables: {
            first: 50,
            teamId: activeTeamId,
            query: {},
            sort: { deviceName: SORT_DIRECTION.ASC },
          },
          fetchPolicy: 'cache-first',
        })
        .catch(() => {
          // Best-effort — don't surface errors to the user.
        });
    };

    // Cleanup is intentionally omitted — the prefetch fires at most once per
    // mount and the underlying `client.query` is cancellation-resilient.
    // Mirrors the convention in `lib/prefetch-routes.ts`.
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run);
    } else {
      // Safari fallback
      setTimeout(run, 2000);
    }
  }, [client, activeTeamId, company, variant]);
}
